import { useEffect, useState, createContext, useContext } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate, useLocation, Link, Outlet } from 'react-router-dom';
import {
  Box,
  Container,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuthStore } from '../../stores/root_store';
import { managerApi, type ManagerUser } from '../../api/manager_api';

// Context to share user data with child routes
interface ManagerUserContextType {
  user: ManagerUser | null;
  setUser: (user: ManagerUser) => void;
  loading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
}

const ManagerUserContext = createContext<ManagerUserContextType | null>(null);

export const useManagerUser = () => {
  const context = useContext(ManagerUserContext);
  if (!context) {
    throw new Error('useManagerUser must be used within ManagerUserPage');
  }
  return context;
};

export const ManagerUserPage = observer(() => {
  const { userKey } = useParams<{ userKey: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const authStore = useAuthStore();

  const [user, setUser] = useState<ManagerUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if current user has manager role
  const currentUserHasManager = authStore.roles.includes('manager');

  // Check if the viewed user is an assistant (is_beta means approved assistant)
  const isAssistant = user?.is_beta ?? false;

  // Determine active tab based on URL
  const getTabValue = () => {
    if (location.pathname.endsWith('/edit')) return 1;
    if (isAssistant && location.pathname.endsWith('/assistant')) return 2;
    if (isAssistant && location.pathname.endsWith('/closed')) return 3;
    if (isAssistant && location.pathname.endsWith('/payouts')) return 4;
    if (!isAssistant && location.pathname.endsWith('/requests')) return 2;
    if (!isAssistant && location.pathname.endsWith('/payments')) return 3;
    return 0;
  };

  useEffect(() => {
    if (!userKey) return;

    const fetchUser = async () => {
      setLoading(true);
      setError(null);
      try {
        const userData = await managerApi.getUser(userKey);
        setUser(userData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load user');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userKey]);

  // Redirect if not a manager
  if (authStore.initialized && !currentUserHasManager) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          You do not have permission to access this page.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !user) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/manager')}
          sx={{ mt: 2 }}
        >
          Back to Search
        </Button>
      </Container>
    );
  }

  return (
    <ManagerUserContext.Provider value={{ user, setUser, loading, error, setError }}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/manager')}
          sx={{ mb: 2 }}
        >
          Back to Search
        </Button>

        <Typography variant="h4" sx={{ mb: 2 }}>
          {user?.first_name || user?.last_name
            ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim()
            : 'User Profile'}
        </Typography>

        <Tabs value={getTabValue()} sx={{ mb: 3 }}>
          <Tab
            label="Overview"
            component={Link}
            to={`/manager/user/${userKey}`}
          />
          <Tab
            label="Edit"
            component={Link}
            to={`/manager/user/${userKey}/edit`}
          />
          {isAssistant && (
            <Tab
              label="Assistant"
              component={Link}
              to={`/manager/user/${userKey}/assistant`}
            />
          )}
          {isAssistant && (
            <Tab
              label="Closed"
              component={Link}
              to={`/manager/user/${userKey}/closed`}
            />
          )}
          {isAssistant && (
            <Tab
              label="Payouts"
              component={Link}
              to={`/manager/user/${userKey}/payouts`}
            />
          )}
          {!isAssistant && (
            <Tab
              label="Requests"
              component={Link}
              to={`/manager/user/${userKey}/requests`}
            />
          )}
          {!isAssistant && (
            <Tab
              label="Payments"
              component={Link}
              to={`/manager/user/${userKey}/payments`}
            />
          )}
        </Tabs>

        <Outlet />
      </Container>
    </ManagerUserContext.Provider>
  );
});
